// Global Instructions Rule Applied!
//
// Agents screen (Operations area, owner-approved mockup v4 + placement
// correction): a BROWSE/AUDIT surface over everything agents have written —
// explicitly NOT a to-do list. Backed by view__agent_written_content; signal
// rows expand to the pilot-format card; maintenance actions stay per-domain.
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Input, Select, Table, Tag } from 'antd'
import { showErrorToast } from '../../core/utils/errorToast.tsx'
import dayjs from 'dayjs'
import ViewToolbar from '../../core/components/ViewToolbar/index.tsx'
import { FormSection } from '../../core/components/FormSection.tsx'
import { store } from '../../state_manager/store.ts'
import { getAccessToken } from '../../core/auth/authService'
import {
  fetchAgentWrittenContent,
  fetchAgentSignals,
  setAgentSignalStatus
} from './services/internalAgentsService.ts'
import {
  AgentSignal,
  AgentWrittenContent
} from '../../state_manager/data_models/internal_agents_model.ts'
import { AgentSignalCard } from './components/AgentSignalCard.tsx'

const TYPE_TAGS: Record<string, { color: string; label: string }> = {
  signal: { color: 'gold', label: 'Signal' },
  memory: { color: 'purple', label: 'Memory' },
  company_brief: { color: 'blue', label: 'Brief' }
}

const STATUS_COLORS: Record<string, string> = {
  open: 'gold',
  acknowledged: 'default',
  dismissed: 'red',
  resolved: 'green',
  active: 'green',
  superseded: 'default',
  archived: 'default',
  live: 'green'
}

const AgentsPage: React.FC = () => {
  const [rows, setRows] = useState<AgentWrittenContent[]>([])
  const [loading, setLoading] = useState(true)
  const [outputType, setOutputType] = useState<string | undefined>(undefined)
  const [status, setStatus] = useState<string | undefined>(undefined)
  const [search, setSearch] = useState('')
  // Expanded signal detail cache: written-content rows are thin; the card
  // needs the evidence join, fetched per company on expand.
  const [signalsBySubject, setSignalsBySubject] = useState<Map<string, AgentSignal[]>>(new Map())
  const token = () => getAccessToken(store.getState().auth)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      setRows(
        await fetchAgentWrittenContent(
          { output_type: outputType, status, limit: 300 },
          token()
        )
      )
      setSignalsBySubject(new Map())
    } catch (error) {
      console.error('[internal-agents] written content load failed:', error)
      showErrorToast(error instanceof Error ? error.message : 'Load failed')
    } finally {
      setLoading(false)
    }
  }, [outputType, status])

  useEffect(() => {
    load()
  }, [load])

  const visible = useMemo(() => {
    const needle = search.trim().toLowerCase()
    if (!needle) return rows
    return rows.filter(
      (row) =>
        (row.subject_name ?? '').toLowerCase().includes(needle) ||
        (row.title ?? '').toLowerCase().includes(needle) ||
        (row.content ?? '').toLowerCase().includes(needle)
    )
  }, [rows, search])

  const ensureSignalsFor = useCallback(
    async (subjectId: string) => {
      if (signalsBySubject.has(subjectId)) return
      try {
        const signals = await fetchAgentSignals(
          { context_table: 'accounts', context_id: subjectId, limit: 500 },
          token()
        )
        setSignalsBySubject((prev) => new Map(prev).set(subjectId, signals))
      } catch (error) {
        console.error('[internal-agents] signal detail load failed:', error)
      }
    },
    [signalsBySubject]
  )

  const handleSignalAction = async (
    signalId: string,
    action: 'acknowledged' | 'dismissed'
  ) => {
    try {
      await setAgentSignalStatus(signalId, action, null, token())
      await load()
    } catch (error) {
      showErrorToast(error instanceof Error ? error.message : 'Failed to update signal')
    }
  }

  return (
    <div data-component='AgentsPage' className='h-full flex flex-col'>
      <ViewToolbar
        moduleKey='agents'
        searchEnabled={false}
        addButtonEnabled={false}
        beforeAddSlot={
          <div className='flex items-center gap-2'>
            <Select
              size='small'
              style={{ minWidth: 130 }}
              placeholder='All output'
              allowClear
              value={outputType}
              onChange={(value) => setOutputType(value)}
              options={[
                { value: 'signal', label: 'Signals' },
                { value: 'memory', label: 'Memories' },
                { value: 'company_brief', label: 'Briefs' }
              ]}
            />
            <Select
              size='small'
              style={{ minWidth: 130 }}
              placeholder='Any status'
              allowClear
              value={status}
              onChange={(value) => setStatus(value)}
              options={[
                { value: 'open', label: 'Open' },
                { value: 'acknowledged', label: 'Acknowledged' },
                { value: 'dismissed', label: 'Dismissed' },
                { value: 'resolved', label: 'Resolved' },
                { value: 'active', label: 'Active (memory)' },
                { value: 'live', label: 'Live (brief)' }
              ]}
            />
            <Input.Search
              size='small'
              style={{ minWidth: 200 }}
              placeholder='Search company / content'
              allowClear
              onSearch={setSearch}
              onChange={(e) => !e.target.value && setSearch('')}
            />
          </div>
        }
        onRefreshData={load}
      />
      <div className='flex-1 min-h-0 overflow-y-auto p-3'>
        <FormSection
          title='Agent-Written Content'
          titleColor='purple'
          loading={loading}
          className='shadow-sm border border-gray-200'
          headerActions={
            <span className='text-gray-400 text-[11.5px]'>
              {visible.length} rows · newest first · browse only — nothing here is assigned to anyone
            </span>
          }
        >
          <Table
            size='small'
            rowKey='id'
            dataSource={visible}
            pagination={{ pageSize: 50, showSizeChanger: false }}
            expandable={{
              rowExpandable: (row: AgentWrittenContent) =>
                row.output_type === 'signal' && !!row.subject_id,
              onExpand: (expanded, row: AgentWrittenContent) => {
                if (expanded && row.subject_id) ensureSignalsFor(row.subject_id)
              },
              expandedRowRender: (row: AgentWrittenContent) => {
                const signals = row.subject_id ? signalsBySubject.get(row.subject_id) : undefined
                const signal = signals?.find((s) => s.agent_signals__id === row.id)
                if (!signal) return <span className='text-gray-400 text-xs'>Loading…</span>
                const ordered = [...(signals ?? [])].sort((a, b) =>
                  a.agent_signals__fired_at.localeCompare(b.agent_signals__fired_at)
                )
                const sequence = ordered.findIndex((s) => s.agent_signals__id === row.id) + 1
                return (
                  <AgentSignalCard
                    signal={signal}
                    sequenceNumber={sequence}
                    maxEvidence={5}
                    onAcknowledge={(id) => handleSignalAction(id, 'acknowledged')}
                    onDismiss={(id) => handleSignalAction(id, 'dismissed')}
                  />
                )
              }
            }}
            columns={[
              {
                title: 'Type',
                dataIndex: 'output_type',
                width: 90,
                render: (value: string) => {
                  const tag = TYPE_TAGS[value] ?? { color: 'default', label: value }
                  return <Tag color={tag.color}>{tag.label}</Tag>
                }
              },
              {
                title: 'Company',
                dataIndex: 'subject_name',
                width: 180,
                render: (value: string | null) => value ?? '—'
              },
              {
                title: 'Title / content',
                render: (_: unknown, row: AgentWrittenContent) => (
                  <div>
                    <div className='font-medium'>{row.title ?? ''}</div>
                    {row.content && row.content !== row.title ? (
                      <div className='text-gray-400 text-xs truncate max-w-xl'>{row.content}</div>
                    ) : null}
                  </div>
                )
              },
              {
                title: 'Severity',
                dataIndex: 'severity',
                width: 80,
                render: (value: string | null) =>
                  value ? (
                    <Tag color={value === 'crit' ? 'red' : value === 'warn' ? 'gold' : 'blue'}>
                      {value}
                    </Tag>
                  ) : (
                    '—'
                  )
              },
              {
                title: 'Status',
                dataIndex: 'status',
                width: 110,
                render: (value: string | null) =>
                  value ? <Tag color={STATUS_COLORS[value] ?? 'default'}>{value}</Tag> : '—'
              },
              {
                title: 'Agent',
                dataIndex: 'agent_type_key',
                width: 140,
                render: (value: string | null) => (
                  <span className='text-xs text-gray-500'>{value ?? '—'}</span>
                )
              },
              {
                title: 'Created',
                dataIndex: 'created_at',
                width: 100,
                render: (value: string) => (
                  <span className='tabular-nums'>{dayjs(value).format('MM/DD/YYYY')}</span>
                )
              }
            ]}
          />
        </FormSection>
      </div>
    </div>
  )
}

export default AgentsPage
